import PageLoader from '@components/PageLoader'
import { pluralize } from '@/utils/plural'
import { useAdminEvents } from '@hooks/useAdminEvents'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AdminHeading } from '../AdminLayout.styles'
import { useAdminFilter } from '../AdminFilterContext'
import ListStats from '../ListStats'
import { formatDate } from '../adminFormat'
import {
	DeletedTag,
	EmptyState,
	ErrorState,
	Muted,
	PageButton,
	PageStatus,
	Pagination,
	ResultCount,
	Row,
	SearchInput,
	SubText,
	Table,
	TableWrap,
	Td,
	Th,
	Toolbar,
} from '../adminList.styles'

const PAGE_SIZE = 25

/**
 * Admin Events list: every itinerary event across the product, searchable by event
 * or trip name, server-paginated, and scoped to the global creation-date range.
 * Rows link to the read-only event detail. Soft-deleted events are flagged.
 */
const EventsPage: React.FC = () => {
	const navigate = useNavigate()
	const { from, to } = useAdminFilter()
	const [searchInput, setSearchInput] = useState('')
	const [debouncedQ, setDebouncedQ] = useState('')
	const [page, setPage] = useState(1)

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedQ(searchInput.trim()), 300)
		return () => clearTimeout(timer)
	}, [searchInput])

	useEffect(() => {
		setPage(1)
	}, [debouncedQ, from, to])

	const { data, isLoading, isError } = useAdminEvents({ page, pageSize: PAGE_SIZE, q: debouncedQ })

	return (
		<>
			<AdminHeading>Events</AdminHeading>

			<Toolbar>
				<SearchInput
					type="search"
					aria-label="Search events by name or trip"
					placeholder="Search by event or trip name…"
					value={searchInput}
					onChange={e => setSearchInput(e.target.value)}
				/>
				{!isLoading && !isError && data && (
					<ResultCount>
						{data.total.toLocaleString()} {pluralize(data.total, 'event')}
					</ResultCount>
				)}
			</Toolbar>

			{isLoading ? (
				<PageLoader label="Loading events…" />
			) : isError || !data ? (
				<ErrorState>Could not load events. Please try again.</ErrorState>
			) : (
				<>
					<ListStats noun="events" total={data.totalAllTime} newInRange={data.total} />

					<TableWrap>
						<Table>
							<thead>
								<tr>
									<Th>Event</Th>
									<Th>Trip</Th>
									<Th>Day</Th>
									<Th>Start</Th>
									<Th>Created</Th>
								</tr>
							</thead>
							<tbody>
								{data.events.length === 0 ? (
									<tr>
										<Td colSpan={5}>
											<EmptyState>No events match this filter.</EmptyState>
										</Td>
									</tr>
								) : (
									data.events.map(event => (
										<Row
											key={event.id}
											$deleted={event.isDeleted}
											onClick={() => navigate(`/admin/events/${event.id}`)}
										>
											<Td>
												{event.name}
												{event.isDeleted && <DeletedTag>Deleted</DeletedTag>}
												<SubText>{event.category}</SubText>
											</Td>
											<Td>{event.tripName}</Td>
											<Td>Day {event.tripDayIndex + 1}</Td>
											<Td>{event.startTime ?? <Muted>—</Muted>}</Td>
											<Td>{formatDate(event.createdAt)}</Td>
										</Row>
									))
								)}
							</tbody>
						</Table>
					</TableWrap>

					<Pagination>
						<PageStatus>
							Page {data.page} of {data.totalPages}
						</PageStatus>
						<PageButton disabled={data.page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
							← Prev
						</PageButton>
						<PageButton disabled={data.page >= data.totalPages} onClick={() => setPage(p => p + 1)}>
							Next →
						</PageButton>
					</Pagination>
				</>
			)}
		</>
	)
}

export default EventsPage
